import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Box, Container, Typography, Stack, Button, Chip, IconButton,
    Paper, Divider, Alert, CircularProgress, TextField, MenuItem,
    Skeleton, Grid, Popover
} from '@mui/material';
import AddIcon            from '@mui/icons-material/Add';
import RemoveIcon         from '@mui/icons-material/Remove';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon   from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon     from '@mui/icons-material/AccessTime';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';
import SportsMotorsportsIcon from '@mui/icons-material/SportsMotorsports';
import LockIcon              from '@mui/icons-material/Lock';
import EventSeatIcon         from '@mui/icons-material/EventSeat';
import ShoppingBagIcon       from '@mui/icons-material/ShoppingBag';
import ArrowBackIcon         from '@mui/icons-material/ArrowBack';
import EastIcon              from '@mui/icons-material/East';

import { useNavigate }     from 'react-router-dom';
import { useTranslation }  from 'react-i18next';
import axiosPublic         from '../api/axiosPublic';
import axiosAuth           from '../api/axiosConfig';
import { useAuth }         from '../context/AuthContext';
import { useCurrency }     from '../context/CurrencyContext';
import { formatCurrency }  from '../utils/currency';
import MiniCalendar        from './MiniCalendar';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix leaflet default marker icon (webpack asset path issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const selectedIcon = new L.Icon({
    iconUrl:       'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize:   [25, 41],
    iconAnchor: [12, 41],
    popupAnchor:[1, -34],
    shadowSize: [41, 41],
});

const TERRACOTTA = '#C4622D';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';
const WALNUT     = '#2C1F14';

const BIKE_TYPES  = ['NORMAL', 'ELECTRIC', 'ROAD', 'MOUNTAIN', 'GRAVEL'];
const BIKE_LABELS = { NORMAL: 'Normal', ELECTRIC: 'Electric', ROAD: 'Road', MOUNTAIN: 'Mountain', GRAVEL: 'Gravel' };

const ADDON_META = {
    HELMET:   { label: 'Helmet',   icon: <SportsMotorsportsIcon sx={{ fontSize: '1rem' }} />, mandatory: false },
    LOCK:     { label: 'Lock',     icon: <LockIcon              sx={{ fontSize: '1rem' }} />, mandatory: true  },
    KID_SEAT: { label: 'Kid Seat', icon: <EventSeatIcon         sx={{ fontSize: '1rem' }} />, mandatory: false },
    BAG:      { label: 'Bag',      icon: <ShoppingBagIcon       sx={{ fontSize: '1rem' }} />, mandatory: false },
};

function generateStartSlots(maxHours = 1) {
    const slots = [];
    const cutoff = 18 * 60 + 30;
    for (let m = 9 * 60; m <= cutoff - maxHours * 60; m += 30) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        slots.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
    }
    return slots;
}

function maxHoursFromStart(startTime) {
    const [h, m] = startTime.split(':').map(Number);
    const startMins = h * 60 + m;
    const cutoff = 18 * 60 + 30;
    return Math.floor((cutoff - startMins) / 60);
}

function buildMadridISO(date, timeStr) {
    const year    = date.getFullYear();
    const dateStr = [
        year,
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-');
    const lastSunMar = new Date(Date.UTC(year, 2, 31 - new Date(Date.UTC(year, 2, 31)).getUTCDay()));
    const lastSunOct = new Date(Date.UTC(year, 9, 31 - new Date(Date.UTC(year, 9, 31)).getUTCDay()));
    const noon   = new Date(`${dateStr}T12:00:00Z`).getTime();
    const offset = noon >= lastSunMar.getTime() && noon < lastSunOct.getTime() ? '+02:00' : '+01:00';
    return `${dateStr}T${timeStr}:00${offset}`;
}

function SectionTitle({ children }) {
    return (
        <Typography variant="overline" fontWeight={800}
                    sx={{ color: TERRACOTTA, letterSpacing: '0.18em', fontSize: '0.72rem', display: 'block', mb: 1.5 }}>
            {children}
        </Typography>
    );
}

export default function RentalBookingPage() {
    const navigate = useNavigate();
    const { t }    = useTranslation();
    const { user } = useAuth();
    const { currency, convert } = useCurrency();

    // ── Remote data ───────────────────────────────────────────────────────────
    const [prices,      setPrices]      = useState([]);
    const [addonPrices, setAddonPrices] = useState([]);
    const [districts,   setDistricts]   = useState([]);
    const [dataLoading, setDataLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            axiosPublic.get('/rentals/prices'),
            axiosPublic.get('/rentals/addon-prices'),
            axiosPublic.get('/districts/view?size=1000'),
        ]).then(([p, a, d]) => {
            setPrices(p.data || []);
            setAddonPrices(a.data || []);
            const distData = d.data;
            setDistricts(Array.isArray(distData) ? distData : (distData?.content || []));
        }).catch(console.error)
          .finally(() => setDataLoading(false));
    }, []);

    // ── Restore saved form state (e.g. after login redirect) ─────────────────
    const savedRental = useMemo(() => {
        try {
            const raw = sessionStorage.getItem('pendingRental');
            if (raw) { sessionStorage.removeItem('pendingRental'); return JSON.parse(raw); }
        } catch { /* ignore */ }
        return null;
    }, []);

    // ── Form state ────────────────────────────────────────────────────────────
    const [districtId,   setDistrictId]  = useState(savedRental?.districtId || '');
    const [bikeQtys,     setBikeQtys]    = useState(savedRental?.bikeQtys || { NORMAL: 0, ELECTRIC: 0, ROAD: 0, MOUNTAIN: 0, GRAVEL: 0 });
    const [durationType, setDurationType] = useState(savedRental?.durationType || null);
    const [selectedDate, setSelectedDate] = useState(savedRental?.selectedDate ? new Date(savedRental.selectedDate) : null);
    const [endDate,      setEndDate]      = useState(savedRental?.endDate ? new Date(savedRental.endDate) : null);
    const [startTime,    setStartTime]    = useState(savedRental?.startTime || null);
    const [hours,        setHours]        = useState(savedRental?.hours || 1);
    const [days,         setDays]         = useState(savedRental?.days || 1);
    const [addons,       setAddons]       = useState(savedRental?.addons || { HELMET: 0, LOCK: 0, KID_SEAT: 0, BAG: 0 });

    // ── Availability ──────────────────────────────────────────────────────────
    // availMap: { [bikeType]: { available: bool, free: number, message: string } }
    const [availMap,      setAvailMap]      = useState({});
    const [checkingAvail, setCheckingAvail] = useState(false);
    const [availChecked,  setAvailChecked]  = useState(false);

    // ── Calendar popover ──────────────────────────────────────────────────────
    const [calAnchorEl, setCalAnchorEl] = useState(null);

    // ── Booking ───────────────────────────────────────────────────────────────
    const [step,    setStep]    = useState(1);
    const [booking, setBooking] = useState(false);
    const [bookErr, setBookErr] = useState('');

    // ── Computed ──────────────────────────────────────────────────────────────
    const totalQty = Object.values(bikeQtys).reduce((s, v) => s + v, 0);

    // For DAILY, derive days from the selected date range (min 1)
    const rangeDays = (selectedDate && endDate)
        ? Math.max(1, Math.round((endDate - selectedDate) / (1000 * 60 * 60 * 24)) + 1)
        : days;

    const units = durationType === 'HOURLY' ? hours : durationType === 'DAILY' ? rangeDays : 0;

    const getPriceConfig = (bikeType) =>
        prices.find(p => p.bikeType === bikeType && p.durationType === durationType) || null;

    const bikeSubtotalCents = useMemo(() => {
        return BIKE_TYPES.reduce((sum, bt) => {
            const qty = bikeQtys[bt];
            if (!qty) return sum;
            const pc = prices.find(p => p.bikeType === bt && p.durationType === durationType);
            return sum + (pc ? Number(pc.pricePerUnit) * qty * units : 0);
        }, 0);
    }, [bikeQtys, prices, durationType, units]);

    const addonSubtotalCents = useMemo(() => {
        return addonPrices.reduce((sum, ac) => {
            const qty = addons[ac.equipmentType] || 0;
            return sum + Number(ac.pricePerUnit) * qty;
        }, 0);
    }, [addonPrices, addons]);

    const depositCents = useMemo(() => {
        return BIKE_TYPES.reduce((sum, bt) => {
            const qty = bikeQtys[bt];
            if (!qty) return sum;
            const pc = prices.find(p => p.bikeType === bt && p.durationType === durationType);
            return sum + (pc ? Number(pc.depositAmount) * qty : 0);
        }, 0);
    }, [bikeQtys, prices, durationType]);

    const totalCents = bikeSubtotalCents + addonSubtotalCents;

    const maxHours    = useMemo(() => startTime ? maxHoursFromStart(startTime) : 9, [startTime]);
    const startSlots  = useMemo(() => {
        const allSlots = generateStartSlots(1);
        if (!selectedDate) return allSlots;
        const today = new Date();
        const isToday = selectedDate.getFullYear() === today.getFullYear() &&
                        selectedDate.getMonth()    === today.getMonth()    &&
                        selectedDate.getDate()     === today.getDate();
        if (!isToday) return allSlots;
        const nowMins = today.getHours() * 60 + today.getMinutes() + 30; // 30-min buffer
        return allSlots.filter(slot => {
            const [sh, sm] = slot.split(':').map(Number);
            return sh * 60 + sm > nowMins;
        });
    }, [selectedDate]);

    const durationLabel = durationType === 'HOURLY'
        ? `${hours} hour${hours !== 1 ? 's' : ''}`
        : durationType === 'DAILY'
        ? `${days} day${days !== 1 ? 's' : ''}`
        : '—';

    const activeTypes = Object.entries(bikeQtys).filter(([, q]) => q > 0);
    const dateReady   = durationType === 'DAILY' ? (!!selectedDate && !!endDate) : !!selectedDate;
    const canProceed  = !!districtId && dateReady && (durationType !== 'HOURLY' || !!startTime) && totalQty > 0 && !!durationType && availChecked &&
        activeTypes.every(([type]) => availMap[type]?.available);

    const selectedDistrict = districts.find(d => d.id === districtId);

    const fmt = (cents) => formatCurrency(convert(cents, 'EUR'), currency);

    // Reset availability when inputs change
    useEffect(() => {
        setAvailChecked(false);
        setAvailMap({});
    }, [districtId, bikeQtys, durationType, selectedDate, startTime, hours, days]);

    // Clamp hours when start time changes
    useEffect(() => {
        if (hours > maxHours) setHours(maxHours);
    }, [maxHours, hours]);

    // If selected time is no longer in available slots (e.g. today's past slots), reset it
    useEffect(() => {
        if (startTime && startSlots.length > 0 && !startSlots.includes(startTime)) {
            setStartTime(null);
        }
    }, [startSlots]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clamp addon quantities when totalQty decreases
    useEffect(() => {
        setAddons(a => {
            const updated = { ...a };
            let changed = false;
            Object.keys(updated).forEach(k => {
                if (updated[k] > totalQty) { updated[k] = totalQty; changed = true; }
            });
            return changed ? updated : a;
        });
    }, [totalQty]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleCheckAvailability = async () => {
        if (!districtId || !selectedDate || totalQty === 0 || (durationType === 'HOURLY' && !startTime)) return;
        setCheckingAvail(true);
        setAvailMap({});
        try {
            const startISO = buildMadridISO(selectedDate, startTime || '09:00');
            const checks = await Promise.all(
                activeTypes.map(([type, qty]) =>
                    axiosPublic.get('/rentals/availability', { params: {
                        districtId, bikeType: type, quantity: qty, durationType,
                        startTime: startISO,
                        ...(durationType === 'HOURLY' ? { hours } : { days: rangeDays }),
                    }}).then(r => ({ type, data: r.data }))
                      .catch(() => ({ type, data: { available: false, message: 'Check failed.' } }))
                )
            );
            const newMap = {};
            checks.forEach(({ type, data }) => {
                const free = data.availableCounts?.[type] ?? 0;
                newMap[type] = { available: data.available, free, message: data.message };
            });
            setAvailMap(newMap);
            setAvailChecked(true);
        } finally {
            setCheckingAvail(false);
        }
    };

    const handleReserve = () => {
        if (!user) {
            sessionStorage.setItem('pendingRental', JSON.stringify({
                districtId, bikeQtys, durationType, selectedDate: selectedDate?.toISOString(),
                endDate: endDate?.toISOString(), startTime, hours, days, addons,
            }));
            navigate('/login');
            return;
        }
        setStep(2);
    };

    const handleConfirm = async () => {
        setBooking(true);
        setBookErr('');
        try {
            const startISO = buildMadridISO(selectedDate, startTime || '09:00');
            const mandatoryAddons = Object.entries(ADDON_META)
                .filter(([type, meta]) => {
                    const ac = addonPrices.find(a => a.equipmentType === type);
                    return (ac ? ac.mandatory : meta.mandatory) && totalQty > 0;
                })
                .map(([type]) => ({ equipmentType: type, quantity: totalQty }));
            const optionalAddons = Object.entries(addons)
                .filter(([, qty]) => qty > 0)
                .map(([equipmentType, qty]) => ({ equipmentType, quantity: qty }));
            const addonsList = [...mandatoryAddons, ...optionalAddons];

            const payload = {
                districtId,
                bikeTypes: activeTypes.map(([bikeType, quantity]) => ({ bikeType, quantity })),
                durationType,
                startTime: startISO,
                ...(durationType === 'HOURLY' ? { hours } : { days: rangeDays }),
                addons:   addonsList,
                currency: currency || 'EUR',
            };

            const result = await axiosAuth.post('/rentals/book', payload);
            navigate(`/booking/${result.data.bill.id}`);
        } catch (err) {
            setBookErr(err.response?.data?.message || 'Booking failed. Please try again.');
            setBooking(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    if (dataLoading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, pt: 6 }}>
                <Container maxWidth="lg">
                    <Skeleton variant="rounded" height={480} sx={{ borderRadius: 3 }} />
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', overflow: 'clip', display: 'flex', flexDirection: 'column', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://www.originalarmenia.com/wp-content/uploads/2020/04/armenian-wine-areni.jpg") center/cover fixed` }}>
            <Helmet>
                <title>Rent a Bike — HayTour</title>
            </Helmet>

            {/* Banner — Step 1 only */}
            {step === 1 && (<Box sx={{ bgcolor: WALNUT, py: 5, px: { xs: 2, md: 6 }, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', right: -40, bottom: -40, opacity: 0.06, pointerEvents: 'none' }}>
                    <DirectionsBikeIcon sx={{ fontSize: '20rem', color: 'white' }} />
                </Box>
                <Typography variant="overline" sx={{ color: GOLD, letterSpacing: '0.22em', fontWeight: 700 }}>
                    Explore at your own pace
                </Typography>
                <Typography variant="h4" fontWeight={800}
                            sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif", mt: 0.5 }}>
                    Rent a Bike
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1, maxWidth: 480 }}>
                    Choose your district, bike types and schedule. Prices update in real time.
                </Typography>
            </Box>)}

            {/* ── STEP 1 ── */}
            {step === 1 && (<Container maxWidth="xl" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start',
                               flexDirection: { xs: 'column', md: 'row' } }}>

                        {/* ══ LEFT — 60% ══ */}
                        <Box sx={{ width: { xs: '100%', md: '60%' }, flexShrink: 0 }}>
                            <Stack spacing={3}>

                                {/* District */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3,
                                    border: '1px solid rgba(196,98,45,0.12)' }}>
                                    <SectionTitle>District</SectionTitle>
                                    <Box sx={{ borderRadius: 2, overflow: 'hidden',
                                        border: '1px solid rgba(196,98,45,0.15)', mb: 1.5,
                                        '& .leaflet-container': { height: 260, width: '100%' } }}>
                                        <MapContainer
                                            center={districts.find(d => d.latitude && d.longitude)
                                                ? [districts.find(d => d.latitude && d.longitude).latitude,
                                                   districts.find(d => d.latitude && d.longitude).longitude]
                                                : [37.39, -5.99]}
                                            zoom={districts.some(d => d.latitude && d.longitude) ? 10 : 8}
                                            scrollWheelZoom={false}
                                            style={{ height: 260, width: '100%' }}>
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            {districts.filter(d => d.latitude && d.longitude).map(d => (
                                                <Marker key={d.id} position={[d.latitude, d.longitude]}
                                                    icon={districtId === d.id ? selectedIcon : new L.Icon.Default()}
                                                    eventHandlers={{ click: () => setDistrictId(d.id) }}>
                                                    <Popup>
                                                        <strong>{d.name}</strong>
                                                        {d.address && <><br />{d.address}</>}
                                                    </Popup>
                                                </Marker>
                                            ))}
                                        </MapContainer>
                                    </Box>
                                    {selectedDistrict && (
                                        <Chip label={selectedDistrict.name}
                                            onDelete={() => setDistrictId('')}
                                            sx={{ bgcolor: 'rgba(196,98,45,0.1)', color: TERRACOTTA,
                                                  fontWeight: 700, mb: 1.5 }} />
                                    )}
                                    <TextField select fullWidth size="small"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        value={districtId} onChange={e => setDistrictId(e.target.value)}
                                        label={districts.some(d => d.latitude && d.longitude)
                                            ? 'Or select from list' : 'Select district'}>
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {districts.map(d => (
                                            <MenuItem key={d.id} value={d.id}>
                                                {d.name}{d.address ? ` — ${d.address}` : ''}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Paper>

                                {/* Duration type */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3,
                                    border: '1px solid rgba(196,98,45,0.12)' }}>
                                    <SectionTitle>Duration Type</SectionTitle>
                                    <Stack direction="row" spacing={2}>
                                        {[
                                            { value: 'HOURLY', label: 'Hourly', sub: 'Pay per hour', icon: <AccessTimeIcon sx={{ fontSize: '2rem' }} />, color: TERRACOTTA },
                                            { value: 'DAILY',  label: 'Daily',  sub: 'Pay per day',  icon: <CalendarMonthIcon sx={{ fontSize: '2rem' }} />, color: FOREST },
                                        ].map(opt => {
                                            const selected = durationType === opt.value;
                                            return (
                                                <Paper key={opt.value} elevation={0} onClick={() => { setDurationType(opt.value); setSelectedDate(null); setEndDate(null); }}
                                                       sx={{
                                                           flex: 1, minHeight: 90, p: 2.5,
                                                           borderRadius: 3, cursor: 'pointer',
                                                           display: 'flex', flexDirection: 'column',
                                                           alignItems: 'center', justifyContent: 'center', gap: 0.8,
                                                           border: `2px solid ${selected ? opt.color : 'rgba(44,31,20,0.12)'}`,
                                                           bgcolor: selected ? (opt.color === TERRACOTTA ? 'rgba(196,98,45,0.08)' : 'rgba(74,103,65,0.08)') : '#fff',
                                                           transition: 'all 0.15s',
                                                           '&:hover': { border: `2px solid ${opt.color}`, bgcolor: opt.color === TERRACOTTA ? 'rgba(196,98,45,0.05)' : 'rgba(74,103,65,0.05)' },
                                                       }}>
                                                    <Box sx={{ color: selected ? opt.color : 'text.disabled' }}>{opt.icon}</Box>
                                                    <Typography fontWeight={800} sx={{ color: selected ? opt.color : WALNUT, fontSize: '0.95rem' }}>
                                                        {opt.label}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">{opt.sub}</Typography>
                                                </Paper>
                                            );
                                        })}
                                    </Stack>
                                    {durationType === null && totalQty > 0 && (
                                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5 }}>
                                            Select a duration type to continue
                                        </Typography>
                                    )}
                                </Paper>

                                {/* Date & Time — 2-column card */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3,
                                    border: '1px solid rgba(196,98,45,0.12)' }}>
                                    <Box sx={{ display: 'flex', gap: 3,
                                               flexDirection: { xs: 'column', md: 'row' } }}>

                                        {/* Left — Calendar (Popover) */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <SectionTitle>Date</SectionTitle>
                                            <Button variant="outlined" startIcon={<CalendarMonthIcon />}
                                                onClick={e => setCalAnchorEl(e.currentTarget)}
                                                sx={{ borderRadius: 2, color: selectedDate ? TERRACOTTA : 'text.secondary',
                                                    borderColor: selectedDate ? TERRACOTTA : 'rgba(44,31,20,0.25)',
                                                    fontWeight: 600, textTransform: 'none' }}>
                                                {durationType === 'DAILY'
                                                    ? selectedDate && endDate
                                                        ? `${selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                                        : selectedDate
                                                            ? `${selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – pick end`
                                                            : 'Pick date range'
                                                    : selectedDate
                                                        ? selectedDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                                                        : 'Pick a date'}
                                            </Button>
                                            <Popover open={Boolean(calAnchorEl)} anchorEl={calAnchorEl}
                                                onClose={() => setCalAnchorEl(null)}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                                PaperProps={{ sx: { borderRadius: 3, p: 1.5, boxShadow: '0 8px 32px rgba(44,31,20,0.15)' } }}>
                                                {durationType === 'DAILY' ? (
                                                    <MiniCalendar
                                                        rangeMode
                                                        maxRange={6}
                                                        value={[selectedDate, endDate]}
                                                        onChange={([start, end]) => {
                                                            setSelectedDate(start);
                                                            setEndDate(end);
                                                            if (start && end) setCalAnchorEl(null);
                                                        }}
                                                        activeDays={null} />
                                                ) : (
                                                    <MiniCalendar value={selectedDate} onChange={d => { setSelectedDate(d); setCalAnchorEl(null); }} activeDays={null} />
                                                )}
                                            </Popover>
                                        </Box>

                                        {/* Right — Start time / Days */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {durationType === 'HOURLY' ? (
                                                <>
                                                    <SectionTitle>Start Time</SectionTitle>
                                                    {!selectedDate ? (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
                                                            Select a date first
                                                        </Typography>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 2.5 }}>
                                                            {startSlots.map(slot => (
                                                                <Chip key={slot} label={slot} size="small"
                                                                      onClick={() => setStartTime(slot)}
                                                                      variant={startTime === slot ? 'filled' : 'outlined'}
                                                                      sx={{
                                                                          fontWeight: 700, fontSize: '0.78rem',
                                                                          bgcolor:    startTime === slot ? TERRACOTTA : 'white',
                                                                          color:      startTime === slot ? 'white' : WALNUT,
                                                                          borderColor: startTime === slot ? TERRACOTTA : 'rgba(44,31,20,0.2)',
                                                                          '&:hover': { bgcolor: startTime === slot ? '#9A4A1F' : 'rgba(44,31,20,0.06)' },
                                                                      }} />
                                                            ))}
                                                        </Box>
                                                    )}

                                                    <SectionTitle>Duration (hours)</SectionTitle>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <IconButton size="small" disabled={hours <= 1}
                                                                    onClick={() => setHours(h => Math.max(1, h - 1))}
                                                                    sx={{ border: '1px solid rgba(196,98,45,0.3)', color: TERRACOTTA }}>
                                                            <RemoveIcon fontSize="small" />
                                                        </IconButton>
                                                        <Typography fontWeight={800} sx={{ minWidth: 28, textAlign: 'center', fontSize: '1.1rem' }}>
                                                            {hours}
                                                        </Typography>
                                                        <IconButton size="small" disabled={hours >= maxHours}
                                                                    onClick={() => setHours(h => Math.min(maxHours, h + 1))}
                                                                    sx={{ border: '1px solid rgba(196,98,45,0.3)', color: TERRACOTTA }}>
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                        {startTime && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                End: {(() => {
                                                                    const [h, m] = startTime.split(':').map(Number);
                                                                    const end = h * 60 + m + hours * 60;
                                                                    return `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
                                                                })()} · max {maxHours}h (ends by 18:30)
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </>
                                            ) : (
                                                <>
                                                    <SectionTitle>Start Time</SectionTitle>
                                                    {startSlots.length > 0 ? (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 2.5 }}>
                                                            {startSlots.map(slot => (
                                                                <Chip key={slot} label={slot} size="small"
                                                                      onClick={() => setStartTime(slot)}
                                                                      variant={startTime === slot ? 'filled' : 'outlined'}
                                                                      sx={{
                                                                          fontWeight: 700, fontSize: '0.78rem',
                                                                          bgcolor:    startTime === slot ? FOREST : 'white',
                                                                          color:      startTime === slot ? 'white' : WALNUT,
                                                                          borderColor: startTime === slot ? FOREST : 'rgba(44,31,20,0.2)',
                                                                          '&:hover': { bgcolor: startTime === slot ? '#3a5233' : 'rgba(44,31,20,0.06)' },
                                                                      }} />
                                                            ))}
                                                        </Box>
                                                    ) : selectedDate ? (
                                                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                                                            No slots available today — select a future date.
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                            Select a date first
                                                        </Typography>
                                                    )}

                                                    <SectionTitle>Duration</SectionTitle>
                                                    {selectedDate && endDate ? (
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography fontWeight={700} sx={{ color: FOREST }}>
                                                                {rangeDays} day{rangeDays !== 1 ? 's' : ''}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">(max 7 days)</Typography>
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Select a date range to see duration (max 7 days)
                                                        </Typography>
                                                    )}
                                                </>
                                            )}
                                        </Box>

                                    </Box>
                                </Paper>

                                {/* Bike types — per-type steppers */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3,
                                    border: '1px solid rgba(196,98,45,0.12)' }}>
                                    <SectionTitle>Bike Types</SectionTitle>
                                    <Box sx={{ border: `1px solid ${STONE}`, borderRadius: 2, overflow: 'hidden' }}>
                                        {BIKE_TYPES.map((bt, i) => {
                                            const pc  = getPriceConfig(bt);
                                            const qty = bikeQtys[bt];
                                            const av  = availMap[bt];
                                            return (
                                                <Box key={bt}
                                                     sx={{ px: 2, py: 1.5,
                                                         borderBottom: i < BIKE_TYPES.length - 1 ? `1px solid ${STONE}` : 'none',
                                                         display: 'flex', alignItems: 'center',
                                                         justifyContent: 'space-between', gap: 1,
                                                         bgcolor: qty > 0 ? 'rgba(74,103,65,0.04)' : 'transparent' }}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                                            {BIKE_LABELS[bt]}
                                                        </Typography>
                                                        {pc && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {fmt(Number(pc.pricePerUnit))}/{durationType === 'HOURLY' ? 'hr' : 'day'}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
                                                        <IconButton size="small" disabled={qty <= 0}
                                                                    onClick={() => setBikeQtys(q => ({ ...q, [bt]: Math.max(0, q[bt] - 1) }))}
                                                                    sx={{ border: '1px solid rgba(196,98,45,0.3)', color: TERRACOTTA, p: 0.4 }}>
                                                            <RemoveIcon fontSize="small" />
                                                        </IconButton>
                                                        <Typography fontWeight={800}
                                                                    sx={{ minWidth: 24, textAlign: 'center', fontSize: '0.95rem' }}>
                                                            {qty}
                                                        </Typography>
                                                        <IconButton size="small"
                                                                    onClick={() => setBikeQtys(q => ({ ...q, [bt]: q[bt] + 1 }))}
                                                                    sx={{ border: '1px solid rgba(196,98,45,0.3)', color: TERRACOTTA, p: 0.4 }}>
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                        {av && (
                                                            <Chip size="small"
                                                                  label={av.available
                                                                      ? `✓ ${av.free} free`
                                                                      : '✗ unavailable'}
                                                                  sx={{ bgcolor: av.available ? 'rgba(74,103,65,0.1)' : 'rgba(198,40,40,0.08)',
                                                                      color: av.available ? FOREST : '#c62828',
                                                                      fontWeight: 700, fontSize: '0.7rem',
                                                                      height: 22 }} />
                                                        )}
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Paper>

                                {/* Add-ons */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3,
                                    border: '1px solid rgba(196,98,45,0.12)' }}>
                                    <SectionTitle>Add-ons</SectionTitle>
                                    <Stack spacing={1.5}>
                                        {Object.entries(ADDON_META).map(([type, meta]) => {
                                            const ac          = addonPrices.find(a => a.equipmentType === type);
                                            const isMandatory = ac ? ac.mandatory : meta.mandatory;
                                            const qty         = addons[type] || 0;

                                            if (isMandatory) {
                                                return (
                                                    <Stack key={type} direction="row"
                                                           alignItems="center" justifyContent="space-between">
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Box sx={{ color: FOREST }}>{meta.icon}</Box>
                                                            <Typography variant="body2" fontWeight={600}>{meta.label}</Typography>
                                                            <Chip label="Included" size="small"
                                                                  sx={{ bgcolor: 'rgba(74,103,65,0.1)', color: FOREST,
                                                                        fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                                                        </Stack>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {totalQty} (1 per bike) fr
                                                        </Typography>
                                                    </Stack>
                                                );
                                            }

                                            return (
                                                <Stack key={type} direction="row"
                                                       alignItems="center" justifyContent="space-between">
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Box sx={{ color: FOREST }}>{meta.icon}</Box>
                                                        <Typography variant="body2" fontWeight={600}>{meta.label}</Typography>
                                                        {ac && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {fmt(Number(ac.pricePerUnit))} each
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                        <IconButton size="small" disabled={qty <= 0}
                                                                    onClick={() => setAddons(a => ({ ...a, [type]: Math.max(0, qty - 1) }))}
                                                                    sx={{ p: 0.3, color: TERRACOTTA }}>
                                                            <RemoveIcon sx={{ fontSize: '1rem' }} />
                                                        </IconButton>
                                                        <Typography fontWeight={700} sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.9rem' }}>
                                                            {qty}
                                                        </Typography>
                                                        <IconButton size="small" disabled={qty >= totalQty}
                                                                    onClick={() => setAddons(a => ({ ...a, [type]: Math.min(totalQty, qty + 1) }))}
                                                                    sx={{ p: 0.3, color: TERRACOTTA }}>
                                                            <AddIcon sx={{ fontSize: '1rem' }} />
                                                        </IconButton>
                                                    </Stack>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                </Paper>

                            </Stack>
                        </Box>

                        {/* ══ RIGHT — 40% — sticky summary ══ */}
                        <Box sx={{ width: { xs: '100%', md: '40%' },
                                   position: { md: 'sticky' }, top: { md: 88 } }}>
                            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden',
                                border: `1px solid rgba(196,98,45,0.18)`,
                                boxShadow: '0 8px 32px rgba(26,26,46,0.1)' }}>

                                {/* Card header */}
                                <Box sx={{ px: 3, py: 2.5,
                                    background: `linear-gradient(135deg, ${WALNUT} 0%, #3A2010 100%)` }}>
                                    <Typography variant="overline"
                                                sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.15em', fontWeight: 700 }}>
                                        Price Summary
                                    </Typography>
                                    <Typography variant="h5" fontWeight={900} sx={{ color: GOLD, mt: 0.5 }}>
                                        {fmt(totalCents)}
                                    </Typography>
                                    {depositCents > 0 && (
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                            + {fmt(depositCents)} deposit (refundable)
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ bgcolor: '#fff', p: 3 }}>

                                    {/* Selection checklist */}
                                    <Stack spacing={0.6} sx={{ mb: 2 }}>
                                        {[
                                            { label: 'District',          done: !!districtId },
                                            { label: 'Duration type',     done: !!durationType },
                                            { label: 'Date & time',       done: !!(selectedDate && startTime) },
                                            { label: 'Bike type selected',done: totalQty > 0 },
                                        ].map(item => (
                                            <Stack key={item.label} direction="row" alignItems="center" spacing={1}>
                                                <CheckCircleIcon sx={{ fontSize: '1rem', color: item.done ? FOREST : 'rgba(0,0,0,0.2)' }} />
                                                <Typography variant="caption" sx={{ color: item.done ? WALNUT : 'text.disabled', fontWeight: item.done ? 700 : 400 }}>
                                                    {item.label}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Per-type breakdown */}
                                    {totalQty > 0 ? (
                                        <Stack spacing={0.8} sx={{ mb: 2 }}>
                                            {activeTypes.map(([bt, qty]) => {
                                                const pc = getPriceConfig(bt);
                                                const subtotal = pc ? Number(pc.pricePerUnit) * qty * units : 0;
                                                return (
                                                    <Stack key={bt} direction="row" justifyContent="space-between">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {BIKE_LABELS[bt]} × {qty} × {durationLabel}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {fmt(subtotal)}
                                                        </Typography>
                                                    </Stack>
                                                );
                                            })}
                                            {Object.entries(ADDON_META).map(([type, meta]) => {
                                                const ac          = addonPrices.find(a => a.equipmentType === type);
                                                const isMandatory = ac ? ac.mandatory : meta.mandatory;
                                                if (isMandatory) {
                                                    if (!totalQty) return null;
                                                    return (
                                                        <Stack key={type} direction="row" justifyContent="space-between">
                                                            <Typography variant="body2" color="text.secondary">
                                                                {meta.label} × {totalQty}
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight={700} sx={{ color: FOREST }}>
                                                                Included
                                                            </Typography>
                                                        </Stack>
                                                    );
                                                }
                                                const qty = addons[type] || 0;
                                                if (!qty) return null;
                                                return (
                                                    <Stack key={type} direction="row" justifyContent="space-between">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {meta.label} × {qty}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {ac ? fmt(Number(ac.pricePerUnit) * qty) : 'TBD'}
                                                        </Typography>
                                                    </Stack>
                                                );
                                            })}
                                            <Divider sx={{ my: 0.5 }} />
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography fontWeight={800}>Total</Typography>
                                                <Typography fontWeight={900} sx={{ color: FOREST, fontSize: '1.05rem' }}>
                                                    {fmt(totalCents)}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                                            Select at least one bike type to see pricing.
                                        </Typography>
                                    )}

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Availability check */}
                                    <Stack spacing={1.5}>
                                        <Button variant="outlined" fullWidth
                                                disabled={!districtId || !selectedDate || totalQty === 0 || checkingAvail}
                                                onClick={handleCheckAvailability}
                                                sx={{ borderRadius: 2, borderColor: FOREST, color: FOREST, fontWeight: 700,
                                                    '&:hover': { borderColor: FOREST, bgcolor: 'rgba(74,103,65,0.06)' } }}>
                                            {checkingAvail
                                                ? <CircularProgress size={20} sx={{ color: FOREST }} />
                                                : 'Check Availability'}
                                        </Button>

                                        {availChecked && activeTypes.length > 0 && (
                                            <Stack spacing={0.5}>
                                                {activeTypes.map(([type]) => {
                                                    const av = availMap[type];
                                                    if (!av) return null;
                                                    return (
                                                        <Alert key={type} severity={av.available ? 'success' : 'error'}
                                                               icon={av.available
                                                                   ? <CheckCircleIcon fontSize="inherit" />
                                                                   : <ErrorOutlineIcon fontSize="inherit" />}
                                                               sx={{ py: 0.3, fontSize: '0.8rem' }}>
                                                            {BIKE_LABELS[type]}: {av.available
                                                                ? `${av.free} available (need ${bikeQtys[type]})`
                                                                : `Only ${av.free} available (need ${bikeQtys[type]})`}
                                                        </Alert>
                                                    );
                                                })}
                                            </Stack>
                                        )}

                                        {!user && availChecked && canProceed && (
                                            <Alert severity="info">
                                                <span>You need to </span>
                                                <a href="/login" style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold', color: 'inherit' }}>log in</a>
                                                <span> to book.</span>
                                            </Alert>
                                        )}

                                        <Button variant="contained" fullWidth endIcon={<EastIcon />}
                                                disabled={!canProceed}
                                                onClick={handleReserve}
                                                sx={{ borderRadius: 2, fontWeight: 800, fontSize: '1rem', py: 1.3,
                                                    background: canProceed
                                                        ? `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`
                                                        : undefined,
                                                    boxShadow: canProceed ? '0 6px 20px rgba(196,98,45,0.4)' : undefined }}>
                                            {!user ? 'Login to Reserve' : 'Reserve'}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Paper>
                        </Box>

                    </Box>
            </Container>)}

            {/* ── STEP 2 — Split-screen confirmation ── */}
            {step === 2 && (
                <Box sx={{ display: 'flex', flexGrow: 1 }}>

                    {/* Left: bike image panel */}
                    <Box sx={{
                        width: '50%',
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        position: 'sticky',
                        top: 0,
                        height: '100vh',
                        flexShrink: 0,
                        overflow: 'hidden',
                    }}>
                        <Box sx={{
                            position: 'absolute', inset: 0,
                            backgroundImage: `url("https://www.originalarmenia.com/wp-content/uploads/2020/04/armenian-wine-areni.jpg")`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }} />
                        <Box sx={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to top, rgba(44,31,20,0.88) 0%, rgba(44,31,20,0.3) 55%, rgba(44,31,20,0.08) 100%)',
                        }} />
                        <Box sx={{ position: 'relative', zIndex: 1, mt: 'auto', p: { md: 4, lg: 5 } }}>
                            <Typography variant="overline"
                                        sx={{ color: 'rgba(255,255,255,0.55)', letterSpacing: 3, fontSize: '0.7rem' }}>
                                Explore at your own pace
                            </Typography>
                            <Typography variant="h4" fontWeight={800} color="#fff"
                                        sx={{ mt: 0.5, fontFamily: "'Playfair Display', Georgia, serif",
                                            fontSize: { md: '1.75rem', lg: '2.125rem' } }}>
                                Rent a Bike
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right: confirmation content */}
                    <Box sx={{
                        width: { xs: '100%', md: '50%' },
                        minHeight: '100vh',
                        bgcolor: PARCHMENT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 6,
                        px: { xs: 2, sm: 4 },
                    }}>
                    <Box sx={{ width: '100%', maxWidth: 520 }}>
                        <Paper elevation={0} sx={{ p: 4, borderRadius: 3,
                            border: '1px solid rgba(196,98,45,0.12)' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                <IconButton size="small" onClick={() => setStep(1)} sx={{ color: TERRACOTTA }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h6" fontWeight={800} sx={{ color: WALNUT }}>
                                    Confirm your booking
                                </Typography>
                            </Stack>

                            <Stack spacing={1.2} sx={{ mb: 3 }}>
                                {[
                                    { label: 'District', value: selectedDistrict?.name || districtId },
                                    { label: 'Type',     value: durationType === 'HOURLY' ? 'Hourly' : 'Daily' },
                                    { label: 'Date', value: durationType === 'DAILY' && endDate
                                        ? `${selectedDate?.toLocaleDateString('en-GB')} – ${endDate.toLocaleDateString('en-GB')}`
                                        : selectedDate?.toLocaleDateString('en-GB') },
                                    { label: 'Duration', value: durationType === 'DAILY'
                                        ? `${rangeDays} day${rangeDays !== 1 ? 's' : ''}`
                                        : durationLabel },
                                    { label: 'Start', value: startTime },
                                ].map(({ label, value }) => (
                                    <Stack key={label} direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                                        <Typography variant="body2" fontWeight={700}>{value}</Typography>
                                    </Stack>
                                ))}

                                {activeTypes.map(([bt, qty]) => (
                                    <Stack key={bt} direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">
                                            {BIKE_LABELS[bt]}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700}>
                                            × {qty} — {fmt((() => {
                                                const pc = getPriceConfig(bt);
                                                return pc ? Number(pc.pricePerUnit) * qty * units : 0;
                                            })())}
                                        </Typography>
                                    </Stack>
                                ))}

                                {Object.entries(ADDON_META).map(([type, meta]) => {
                                    const ac          = addonPrices.find(a => a.equipmentType === type);
                                    const isMandatory = ac ? ac.mandatory : meta.mandatory;
                                    if (isMandatory) {
                                        return (
                                            <Stack key={type} direction="row" justifyContent="space-between">
                                                <Typography variant="body2" color="text.secondary">{meta.label}</Typography>
                                                <Typography variant="body2" fontWeight={700} sx={{ color: FOREST }}>
                                                    × {totalQty} — Included
                                                </Typography>
                                            </Stack>
                                        );
                                    }
                                    const qty = addons[type] || 0;
                                    if (!qty) return null;
                                    return (
                                        <Stack key={type} direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">{meta.label}</Typography>
                                            <Typography variant="body2" fontWeight={700}>
                                                × {qty} — {ac ? fmt(Number(ac.pricePerUnit) * qty) : 'TBD'}
                                            </Typography>
                                        </Stack>
                                    );
                                })}

                                <Divider />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography fontWeight={800}>Total</Typography>
                                    <Typography fontWeight={900} sx={{ color: FOREST, fontSize: '1.1rem' }}>
                                        {fmt(totalCents)}
                                    </Typography>
                                </Stack>
                                {depositCents > 0 && (
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">Deposit (held)</Typography>
                                        <Typography variant="body2" fontWeight={600}>{fmt(depositCents)}</Typography>
                                    </Stack>
                                )}
                            </Stack>

                            {bookErr && <Alert severity="error" sx={{ mb: 2 }}>{bookErr}</Alert>}

                            <Button variant="contained" fullWidth disabled={booking}
                                    onClick={handleConfirm}
                                    sx={{ borderRadius: 2, fontWeight: 800, fontSize: '1rem', py: 1.4,
                                        background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                        boxShadow: '0 6px 20px rgba(196,98,45,0.4)' }}>
                                {booking ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Confirm Booking'}
                            </Button>
                        </Paper>
                    </Box>
                    </Box>
                </Box>
            )}

        </Box>
    );
}
